import { useEffect, useRef } from 'react';
import { subscribeWaitGateTick } from '../components/SprintDetail/shared/ConditionsDropdown/utils/useWaitGateNow';
import { useAuth } from '../components/AuthProvider';
import { NOTIFICATION_TYPE } from '../notifications';
import {
  applyReminderPatches,
  collectAvailableReminderUnlocks,
  collectDueReminders,
  collectNewWhenAvailableOnUnlocked,
  isHoldReminderAsked,
  remindersEqual,
} from '../utils/missionReminders';
import { isWaitFulfilled } from '../utils/waitFor';
import { catchUpRecurring } from '../utils/recurringSchedule';
import { notifyMissionInbox } from '../utils/notificationInbox';

function fireAvailable(task, reminders, ctx) {
  const note = reminders.find((r) => r.note)?.note;
  notifyMissionInbox({
    ...ctx,
    type: NOTIFICATION_TYPE.MISSION_AVAILABLE,
    task,
    note,
  });
  return reminders.map((r) => r.id);
}

/**
 * Fires opt-in reminders into the bell while the campaign is open.
 * Timed reminders fire when the clock arrives (including already-due on open).
 * A recurring reminder stays on the mission and moves to its next occurrence.
 * "When available" fires on lock → unlock, or immediately if set on an
 * unlocked mission. Same event as a time-gate ping — one inbox row.
 */
export function useMissionReminderWatch({
  tasks = [],
  lockStates = {},
  now: _now,
  campaignId,
  campaignName,
  breadcrumbPath = [],
  enabled = true,
  onRemindersChange,
}) {
  const { user, workspaceId } = useAuth();
  const campaignRef = useRef(campaignId);
  const prevLockedRef = useRef(null);
  const prevWhenAvailableRef = useRef(null);
  const firedTimedRef = useRef(new Set());

  useEffect(() => {
    if (campaignRef.current !== campaignId) {
      prevLockedRef.current = null;
      prevWhenAvailableRef.current = null;
      firedTimedRef.current = new Set();
      campaignRef.current = campaignId;
    }
  }, [campaignId]);

  const runRef = useRef(() => {});
  runRef.current = () => {
    if (!enabled || !campaignId || !onRemindersChange) return;
    const at = Date.now();

    const ctx = {
      userEmail: user?.email || '',
      workspaceId,
      campaignId,
      campaignName,
      breadcrumbPath,
    };

    const due = collectDueReminders(tasks, at);
    const patchByTask = new Map();
    const queuePatch = (taskId, id, next) => {
      const patches = patchByTask.get(taskId) || [];
      patches.push({ id, next });
      patchByTask.set(taskId, patches);
    };

    for (const { task, reminder } of due) {
      const fireKey = `${reminder.id}:${reminder.at}`;
      if (firedTimedRef.current.has(fireKey)) continue;
      firedTimedRef.current.add(fireKey);
      if (reminder.hold) {
        if (isWaitFulfilled(task) || isHoldReminderAsked(reminder)) continue;
        const details = String(task.waitDetails || '').trim();
        notifyMissionInbox({
          ...ctx,
          type: NOTIFICATION_TYPE.WAIT_FOR,
          task,
          reminderId: reminder.id,
          occurrenceAt: reminder.at,
          note: details || undefined,
        });
        queuePatch(task.id, reminder.id, {
          ...reminder,
          askedAt: new Date(at).toISOString(),
        });
        continue;
      }
      if (reminder.recur) {
        const result = catchUpRecurring(reminder, at);
        if (result.notify) {
          notifyMissionInbox({
            ...ctx,
            type: NOTIFICATION_TYPE.REMINDER_AT_TIME,
            task,
            reminderId: reminder.id,
            occurrenceAt: result.occurrenceAt,
            note: reminder.note || undefined,
          });
        }
        queuePatch(task.id, reminder.id, result.reminder);
        continue;
      }
      const stillLocked = Boolean(lockStates?.[task.id]?.isLocked);
      const unavailableNote = reminder.forStart && stillLocked
        ? 'Reminder for the start time. This mission is still unavailable.'
        : '';
      const note = [reminder.note, unavailableNote].filter(Boolean).join('\n');
      notifyMissionInbox({
        ...ctx,
        type: NOTIFICATION_TYPE.REMINDER_AT_TIME,
        task,
        reminderId: reminder.id,
        note: note || undefined,
      });
      queuePatch(task.id, reminder.id, null);
    }

    const { nextLockedIds, unlocked } = collectAvailableReminderUnlocks(
      prevLockedRef.current,
      lockStates,
      tasks
    );
    prevLockedRef.current = nextLockedIds;
    for (const { task, reminders } of unlocked) {
      const ids = fireAvailable(task, reminders, ctx);
      ids.forEach((id) => queuePatch(task.id, id, null));
    }

    const { nextIdsByTask, fires } = collectNewWhenAvailableOnUnlocked(
      prevWhenAvailableRef.current,
      tasks,
      lockStates
    );
    prevWhenAvailableRef.current = nextIdsByTask;
    for (const { task, reminders } of fires) {
      const ids = fireAvailable(task, reminders, ctx);
      ids.forEach((id) => queuePatch(task.id, id, null));
    }

    for (const [taskId, patches] of patchByTask) {
      const task = tasks.find((t) => t.id === taskId);
      if (!task) continue;
      const next = applyReminderPatches(task.reminders, patches);
      if (remindersEqual(task.reminders, next)) continue;
      onRemindersChange(taskId, next, { silent: true });
    }
  };

  useEffect(() => {
    if (!enabled || !campaignId || !onRemindersChange) return undefined;
    runRef.current();
    return subscribeWaitGateTick(() => runRef.current());
  }, [
    tasks,
    lockStates,
    campaignId,
    campaignName,
    breadcrumbPath,
    enabled,
    onRemindersChange,
    user?.email,
    workspaceId,
  ]);
}
